import { preEventCountdown } from './pre-event-countdown.js';

export interface PreEventShare {
  video: MediaStreamTrack;
  audio: MediaStreamTrack | null;
  dispose(): void;
}

/** Build a local presentation. Only its media tracks are sent to the room. */
export async function createPreEventShare(options: {
  startsAt: string;
  title: string;
  showTitle: boolean;
  image: File | null;
  audio: File | null;
  ambient: boolean;
  capturedAudio?: MediaStream | null;
  loop: boolean;
}): Promise<PreEventShare> {
  const start = Date.parse(options.startsAt);
  if (!Number.isFinite(start) || start <= Date.now()) throw new Error('Alege o oră de începere viitoare.');
  if (options.image && (!options.image.type.startsWith('image/') || options.image.size > 15 * 1024 * 1024)) throw new Error('Alege o imagine de maximum 15 MB.');
  if (options.audio && (!options.audio.type.startsWith('audio/') || options.audio.size > 100 * 1024 * 1024)) throw new Error('Alege un fișier audio de maximum 100 MB.');
  const canvas = document.createElement('canvas'); canvas.width = 1280; canvas.height = 720;
  if (typeof canvas.captureStream !== 'function') throw new Error('Acest dispozitiv nu poate publica prezentarea.');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Prezentarea nu a putut fi pregătită.');
  let picture: ImageBitmap | null = null, audioContext: AudioContext | null = null;
  let player: HTMLAudioElement | null = null, audioUrl: string | null = null;
  let timer: ReturnType<typeof setInterval> | null = null, musicTimer: ReturnType<typeof setInterval> | null = null, stream: MediaStream | null = null;
  let audioTrack: MediaStreamTrack | null = null;
  const oscillators: OscillatorNode[] = [];
  let disposed = false;
  const dispose = () => {
    if (disposed) return; disposed = true;
    if (timer) clearInterval(timer);
    if (musicTimer) clearInterval(musicTimer);
    audioTrack?.stop();
    options.capturedAudio?.getTracks().forEach(track => track.stop());
    player?.pause(); if (player) { player.removeAttribute('src'); player.load(); }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    oscillators.forEach(osc => { try { osc.stop(); } catch (_) {} });
    stream?.getTracks().forEach(track => { track.onended = null; track.stop(); });
    picture?.close(); if (audioContext) void audioContext.close().catch(() => {});
  };
  try {
    if (options.image) picture = await createImageBitmap(options.image);
    const paint = () => {
      if (picture) {
        const scale = Math.min(canvas.width / picture.width, canvas.height / picture.height);
        const w = picture.width * scale, h = picture.height * scale;
        context.fillStyle = '#091225'; context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(picture, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      } else {
        const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, '#0c1834'); gradient.addColorStop(0.55, '#29245d'); gradient.addColorStop(1, '#176b88');
        context.fillStyle = gradient; context.fillRect(0, 0, canvas.width, canvas.height);
      }
      const raw = preEventCountdown(options.startsAt) || '00:00';
      const compact = raw.startsWith('00:') ? raw.slice(3) : raw;
      const x = 1175, y = 105, radius = 68;
      context.save();
      context.shadowColor = 'rgba(0, 0, 0, .4)'; context.shadowBlur = 18;
      context.beginPath(); context.arc(x, y, radius, 0, Math.PI * 2);
      context.fillStyle = 'rgba(7, 17, 38, .58)'; context.fill();
      context.shadowBlur = 0; context.lineWidth = 7;
      const ring = context.createLinearGradient(x-radius, y-radius, x+radius, y+radius);
      ring.addColorStop(0, '#a9e6ff'); ring.addColorStop(.5, '#268dff'); ring.addColorStop(1, '#655bff');
      context.strokeStyle = ring; context.stroke();
      context.beginPath(); context.arc(x, y, radius - 9, 0, Math.PI * 2);
      context.lineWidth = 1; context.strokeStyle = 'rgba(190,225,255,.4)'; context.stroke();
      context.textAlign = 'center'; context.textBaseline = 'middle'; context.fillStyle = '#fff';
      context.font = compact.length > 8 ? '700 19px sans-serif' : compact.length > 5 ? '700 25px sans-serif' : '700 34px sans-serif';
      context.fillText(compact, x, y, radius * 1.75);
      if (options.showTitle && options.title.trim()) {
        context.font = '600 23px sans-serif';
        context.fillText(options.title.trim().slice(0, 60), canvas.width / 2, 675, 1100);
      }
      context.restore();
    };
    paint(); timer = setInterval(paint, 1000); stream = canvas.captureStream(5);
    let audio: MediaStreamTrack | null = null;
    if (options.capturedAudio) {
      audio = options.capturedAudio.getAudioTracks()[0] || null; audioTrack = audio;
      if (!audio) throw new Error('Fila selectata nu furnizeaza sunet.');
    } else if (options.audio || options.ambient) {
      audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();
      if (options.audio) {
        audioUrl = URL.createObjectURL(options.audio); player = new Audio(audioUrl); player.loop = options.loop;
        const source = audioContext.createMediaElementSource(player); source.connect(destination);
        await audioContext.resume(); await player.play();
      } else {
        const master = audioContext.createGain(); master.gain.value = 0.035; master.connect(destination);
        await audioContext.resume();
        const playPhrase = () => {
          if (!audioContext || disposed) return;
          for (const [index, frequency] of [261.63, 329.63, 392, 329.63].entries()) {
            const osc = audioContext.createOscillator(); const envelope = audioContext.createGain();
            const at = audioContext.currentTime + index * 0.85;
            osc.type = 'sine'; osc.frequency.value = frequency;
            envelope.gain.setValueAtTime(0.001, at); envelope.gain.exponentialRampToValueAtTime(0.6, at + 0.12);
            envelope.gain.exponentialRampToValueAtTime(0.001, at + 0.8);
            osc.connect(envelope); envelope.connect(master); osc.start(at); osc.stop(at + 0.81);
            oscillators.push(osc); osc.onended = () => { const i=oscillators.indexOf(osc); if(i>=0) oscillators.splice(i,1); };
          }
        };
        playPhrase(); if (options.loop) musicTimer = setInterval(playPhrase, 4000);
      }
      audio = destination.stream.getAudioTracks()[0] || null; audioTrack = audio;
      if (!audio) throw new Error('Sunetul prezentării nu poate fi creat pe acest dispozitiv.');
    }
    const video = stream.getVideoTracks()[0];
    if (!video) throw new Error('Imaginea prezentării nu poate fi creată pe acest dispozitiv.');
    return {video,audio,dispose};
  } catch (error) { dispose(); throw error; }
}
